/**
 * ==============================================================================
 * product-main-upload.js
 * ------------------------------------------------------------------------------
 * Handles the Drag & Drop Custom Photo Upload component, synchronizing uploaded
 * files with the checkout cart payload, live gallery preview, and wallpaper customizer.
 * ==============================================================================
 */

(function () {
  'use strict';

  function formatBytes(bytes, decimals = 1) {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  const OVER_20MB_TARGET_SELECTOR =
    '#ProductInfo-template--27716240736567__main > p.product__text.product__text--text_VPVq43.inline-richtext > a, ' +
    '[id^="ProductInfo-"] > .product__text--text_VPVq43 > a, ' +
    '.product__text--text_VPVq43 > a, ' +
    'a[href*="dropbox.com/request"]';

  function getOver20MbTarget() {
    return document.querySelector(OVER_20MB_TARGET_SELECTOR);
  }

  function highlightOver20MbWarning(file) {
    const targetLink = getOver20MbTarget();
    if (targetLink) {
      targetLink.classList.add('is-over20mb-warning');
      targetLink.setAttribute('aria-invalid', 'true');
      const parentP = targetLink.closest('.product__text--text_VPVq43');
      if (parentP) parentP.classList.add('is-over20mb-warning');
    }

    const dndContainer = document.querySelector('[data-wm-drag-drop]');
    if (dndContainer) {
      const maxSizeMb = parseFloat(dndContainer.dataset.maxSizeMb) || 20;
      const sizeStr = file && file.size ? ` (${formatBytes(file.size)})` : '';
      const warningTemplate = dndContainer.dataset.warningOver20mb || 'File exceeds {{ max }}MB{{ size }}. Please use the highlighted direct upload link below for larger files (PSB, PSD, TIFF, PDF).';
      const warningMsg = warningTemplate.replace('{{ max }}', maxSizeMb).replace('{{ size }}', sizeStr);

      dndContainer.classList.add('has-warning');
      dndContainer.setAttribute('aria-invalid', 'true');
      const infoEl = dndContainer.querySelector('[data-wm-info], .wm-drag-drop__info');
      if (infoEl) {
        infoEl.textContent = warningMsg;
        infoEl.classList.add('is-error');
      }
      const statusLiveEl = dndContainer.querySelector('[data-wm-status]');
      if (statusLiveEl) {
        statusLiveEl.textContent = warningMsg;
      }
    }

    if (targetLink) {
      setTimeout(() => {
        const rect = targetLink.getBoundingClientRect();
        const offset = 140;
        const targetY = window.scrollY + rect.top - offset;
        window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
        targetLink.focus({ preventScroll: true });
      }, 150);
    }
  }

  function clearOver20MbWarning() {
    const targetLinks = document.querySelectorAll(OVER_20MB_TARGET_SELECTOR);
    targetLinks.forEach((link) => {
      link.classList.remove('is-over20mb-warning');
      link.removeAttribute('aria-invalid');
      const parentP = link.closest('.product__text--text_VPVq43');
      if (parentP) parentP.classList.remove('is-over20mb-warning');
    });
  }

  window.highlightOver20MbWarning = highlightOver20MbWarning;
  window.clearOver20MbWarning = clearOver20MbWarning;

  function initDragDropUpload() {
    const dndContainer = document.querySelector('[data-wm-drag-drop]');
    if (!dndContainer) return;

    const fileInput = dndContainer.querySelector('input[type="file"]#file-upload');
    const dropZone = dndContainer.querySelector('[data-wm-drop-zone]');
    const dropTrigger = dndContainer.querySelector('[data-wm-drop-trigger]');
    const loadingEl = dndContainer.querySelector('[data-wm-loading]');
    const previewEl = dndContainer.querySelector('[data-wm-preview]');
    let thumbImg = dndContainer.querySelector('[data-wm-thumb]');
    const filenameEl = dndContainer.querySelector('[data-wm-filename]');
    const filesizeEl = dndContainer.querySelector('[data-wm-filesize]');
    const changeBtn = dndContainer.querySelector('[data-wm-change-btn]');
    const statusLiveEl = dndContainer.querySelector('[data-wm-status]');
    const infoEl = dndContainer.querySelector('[data-wm-info], .wm-drag-drop__info');
    const originalInfoText = infoEl ? (infoEl.dataset.originalText || infoEl.textContent) : '';

    if (!fileInput) return;

    const maxSizeMb = parseFloat(dndContainer.dataset.maxSizeMb) || 20;
    const maxSizeBytes = maxSizeMb * 1024 * 1024;
    let currentObjectUrl = null;
    let currentFile = null;

    function openFilePicker(e) {
      if (e) e.stopPropagation();
      fileInput.click();
    }

    function showLoading() {
      if (dropZone) dropZone.style.display = 'none';
      if (previewEl) previewEl.style.display = 'none';
      const existingThumb = dndContainer.querySelector('[data-wm-thumb]');
      if (existingThumb) existingThumb.remove();
      if (loadingEl) loadingEl.style.display = 'flex';
    }

    function showPreview(file, objectUrl) {
      if (loadingEl) loadingEl.style.display = 'none';
      if (dropZone) dropZone.style.display = 'none';
      if (previewEl) previewEl.style.display = 'block';

      let currentThumb = dndContainer.querySelector('[data-wm-thumb]');
      if (!currentThumb) {
        currentThumb = document.createElement('img');
        currentThumb.className = 'wm-drag-drop__thumb';
        currentThumb.setAttribute('data-wm-thumb', '');
        currentThumb.width = 64;
        currentThumb.height = 64;
        currentThumb.loading = 'lazy';
        const thumbWrapper = dndContainer.querySelector('.wm-drag-drop__thumb-wrapper');
        if (thumbWrapper) {
          thumbWrapper.insertBefore(currentThumb, thumbWrapper.firstChild);
        }
      }
      currentThumb.src = objectUrl;
      currentThumb.alt = file.name || 'Uploaded custom photo preview';
      if (filenameEl) filenameEl.textContent = file.name;
      if (filesizeEl) filesizeEl.textContent = formatBytes(file.size);

      if (statusLiveEl) {
        const successTemplate = dndContainer.dataset.statusSuccess || 'Photo {{ name }} uploaded successfully.';
        statusLiveEl.textContent = successTemplate.replace('{{ name }}', file.name);
      }
    }

    function resetToIdle(clearError = true) {
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
        currentObjectUrl = null;
      }
      fileInput.value = '';
      
      localStorage.removeItem('wm_custom_photo_data');
      localStorage.removeItem('wm_custom_photo_name');
      localStorage.removeItem('wm_custom_photo_type');
      
      window.hasUploadedCustomPhoto = false;
      window.currentCustomPhotoFile = null;
      window.currentCustomPhotoUrl = null;

      if (previewEl) previewEl.style.display = 'none';
      const currentThumb = dndContainer.querySelector('[data-wm-thumb]');
      if (currentThumb) currentThumb.remove();
      if (loadingEl) loadingEl.style.display = 'none';
      if (dropZone) dropZone.style.display = 'flex';
      dndContainer.classList.remove('has-file', 'is-dragover');

      if (clearError) {
        dndContainer.classList.remove('has-warning');
        dndContainer.setAttribute('aria-invalid', 'false');
        if (infoEl) {
          infoEl.textContent = originalInfoText;
          infoEl.classList.remove('is-error');
        }
        clearOver20MbWarning();
      }

      const beforeAfterEls = document.querySelectorAll('.product-media-before-after, [id^="ProductMediaBeforeAfter-"]');
      beforeAfterEls.forEach((el) => {
        el.classList.remove('is-hidden');
        el.style.removeProperty('display');
      });
      const galleryEls = document.querySelectorAll('.product-media-swiper-wrapper, [id^="ProductMediaSwiper-"], [id^="ProductMediaGalleryContainer-"]');
      if (beforeAfterEls.length > 0) {
        galleryEls.forEach((el) => {
          el.classList.add('is-hidden');
          el.style.setProperty('display', 'none', 'important');
        });
      }

      // Restore original gallery images if saved
      const galleryImages = document.querySelectorAll(
        '[id^="ProductMediaSwiper-"] img, .swiper-main-image, .product-media-single img, .product__media img, .product-media-modal img'
      );
      galleryImages.forEach((img) => {
        if (img.dataset.originalSrc) {
          img.src = img.dataset.originalSrc;
          img.srcset = img.dataset.originalSrcset || '';
        }
      });

      if (statusLiveEl) {
        statusLiveEl.textContent = dndContainer.dataset.statusReset || 'Upload reset. Ready for image.';
      }

      document.dispatchEvent(new CustomEvent('custom-photo:cleared', { bubbles: true }));
      if (typeof window.refreshInlineCustomizer === 'function') {
        window.refreshInlineCustomizer();
      }
    }

    function showError(message) {
      dndContainer.classList.add('has-warning');
      dndContainer.setAttribute('aria-invalid', 'true');
      if (infoEl) {
        infoEl.textContent = message;
        infoEl.classList.add('is-error');
      }
      if (statusLiveEl) {
        statusLiveEl.textContent = message;
      }
      resetToIdle(false);
    }

    function processFile(file, isRestore = false) {
      if (!file) return;
      currentFile = file;
      dndContainer.classList.remove('has-warning');
      clearOver20MbWarning();

      // 1. File Type Validation (Shopify supported image formats)
      const isImage = file.type.startsWith('image/') || /\.(jpe?g|jfif|png|webp|avif|heic|heif|gif)$/i.test(file.name);
      if (!isImage) {
        const typeError = dndContainer.dataset.errorType || 'Please upload a valid image file (JPG, PNG, WEBP, AVIF, HEIC).';
        showError(typeError);
        return;
      }

      // 2. File Size Validation (Shopify 20MB limit)
      if (file.size > maxSizeBytes) {
        highlightOver20MbWarning(file);
        resetToIdle(false);
        return;
      }

      if (!isRestore) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            localStorage.setItem('wm_custom_photo_data', e.target.result);
            localStorage.setItem('wm_custom_photo_name', file.name);
            localStorage.setItem('wm_custom_photo_type', file.type);
          } catch (err) {
            console.warn('Could not save photo to localStorage (quota exceeded)', err);
          }
        };
        reader.readAsDataURL(file);
      }

      dndContainer.setAttribute('aria-invalid', 'false');
      if (infoEl) {
        infoEl.textContent = originalInfoText;
        infoEl.classList.remove('is-error');
      }

      // 3. Sync to File Input via DataTransfer for Cart Line Item Properties
      try {
        const dt = new DataTransfer();
        dt.items.add(file);
        fileInput.files = dt.files;
      } catch (err) {
        console.warn('DataTransfer sync error:', err);
      }

      showLoading();

      // Revoke previous object URL if any
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
      }
      currentObjectUrl = URL.createObjectURL(file);
      window.currentCustomPhotoFile = file;
      window.currentCustomPhotoUrl = currentObjectUrl;
      window.hasUploadedCustomPhoto = true;
      if (typeof window.refreshInlineCustomizer === 'function') {
        window.refreshInlineCustomizer();
      }

      setTimeout(() => {
        showPreview(file, currentObjectUrl);
        dndContainer.classList.add('has-file');

        // 4. Reveal gallery and hide Before & After block
        const beforeAfterEls = document.querySelectorAll('.product-media-before-after, [id^="ProductMediaBeforeAfter-"]');
        beforeAfterEls.forEach((el) => {
          el.classList.add('is-hidden');
          el.style.setProperty('display', 'none', 'important');
        });
        const galleryEls = document.querySelectorAll('.product-media-swiper-wrapper, [id^="ProductMediaSwiper-"], [id^="ProductMediaGalleryContainer-"]');
        galleryEls.forEach((el) => {
          el.classList.remove('is-hidden');
          el.style.removeProperty('display');
        });

        // Ensure zoom script is loaded for lightbox
        const swiperWrapperEl = document.querySelector('.product-media-swiper-wrapper');
        const zoomScriptUrl = swiperWrapperEl?.getAttribute('data-zoom-script-url');
        if (zoomScriptUrl && !document.querySelector(`script[src="${zoomScriptUrl}"]`)) {
          const script = document.createElement('script');
          script.src = zoomScriptUrl;
          script.defer = true;
          document.body.appendChild(script);
        }

        // Update Main Product Gallery Swiper & Standard Media Images
        const galleryImages = document.querySelectorAll(
          '[id^="ProductMediaSwiper-"] img, .swiper-main-image, .product-media-single img, .product__media img, .product-media-modal img'
        );
        galleryImages.forEach((img) => {
          if (!img.dataset.originalSrc) {
            img.dataset.originalSrc = img.src;
            img.dataset.originalSrcset = img.srcset || '';
          }
          img.src = currentObjectUrl;
          img.srcset = '';
          img.alt = file.name;
        });

        // Ensure zoom button exists in .product-media-single if absent
        const singleContainer = document.querySelector('.product-media-single');
        if (singleContainer && !singleContainer.querySelector('.product-media-zoom-btn')) {
          const zoomBtn = document.createElement('button');
          zoomBtn.className = 'product-media-zoom-btn';
          zoomBtn.type = 'button';
          zoomBtn.setAttribute('aria-haspopup', 'dialog');
          zoomBtn.setAttribute('aria-label', 'Open media in modal');
          zoomBtn.innerHTML = `<span class="svg-wrapper"><svg aria-hidden="true" focusable="false" class="icon icon-zoom" fill="none" viewBox="0 0 16 16"><path d="M11 11L14.5 14.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="7" cy="7" r="5" stroke="currentColor" stroke-width="1.5"/></svg></span>`;
          singleContainer.insertBefore(zoomBtn, singleContainer.firstChild);
        }

        const mainSwiperEl = document.querySelector('.product-media-swiper-main');
        if (mainSwiperEl && mainSwiperEl.swiper) {
          mainSwiperEl.swiper.slideTo(0);
          mainSwiperEl.swiper.update();
        }

        // 5. Update Wallpaper Customizer Artworks
        const artImgs = document.querySelectorAll('.wm-artwork, [data-wm-artwork]');
        artImgs.forEach((img) => {
          img.removeAttribute('crossorigin'); // Prevent CORS taint when rendering canvas
          img.src = currentObjectUrl;
          img.srcset = '';
          img.addEventListener('load', () => {
            if (typeof window.refreshInlineCustomizer === 'function') {
              window.refreshInlineCustomizer();
            }
          }, { once: true });
        });

        // 6. Smooth scroll and focus on Width Input element (#width_input_10023246037303)
        if (!isRestore) {
          const widthInput = document.querySelector('#width_input_10023246037303, input[id^="width_input_"], [data-wm-width]');
          if (widthInput) {
            setTimeout(() => {
              const offset = 80;
              const y = widthInput.getBoundingClientRect().top + window.scrollY - offset;
              window.scrollTo({ top: y, behavior: 'smooth' });
              widthInput.focus({ preventScroll: true });
            }, 300);
          }
        }

        // Dispatch custom notification event
        document.dispatchEvent(
          new CustomEvent('custom-photo:uploaded', {
            bubbles: true,
            detail: { file, url: currentObjectUrl }
          })
        );
      }, 250);
    }

    // Drag & Drop Listeners
    ['dragenter', 'dragover'].forEach((eventName) => {
      dndContainer.addEventListener(eventName, (e) => {
        e.preventDefault();
        e.stopPropagation();
        dndContainer.classList.add('is-dragover');
      });
    });

    ['dragleave', 'dragend'].forEach((eventName) => {
      dndContainer.addEventListener(eventName, (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Check if leaving to outside the container
        if (!dndContainer.contains(e.relatedTarget)) {
          dndContainer.classList.remove('is-dragover');
        }
      });
    });

    dndContainer.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      dndContainer.classList.remove('is-dragover');

      const dt = e.dataTransfer;
      if (dt && dt.files && dt.files.length > 0) {
        processFile(dt.files[0]);
      }
    });

    // Click & Keyboard Triggers
    if (dropZone) {
      dropZone.addEventListener('click', openFilePicker);
      dropZone.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openFilePicker();
        }
      });
    }

    if (dropTrigger) {
      dropTrigger.addEventListener('click', openFilePicker);
    }

    if (changeBtn) {
      changeBtn.addEventListener('click', openFilePicker);
    }

    fileInput.addEventListener('change', (e) => {
      if (e.target.files && e.target.files.length > 0) {
        processFile(e.target.files[0]);
      }
    });

    function checkSampleIntent() {
      const urlParams = new URLSearchParams(window.location.search);
      const isSampleIntent = urlParams.get('intent') === 'sample' || window.location.hash === '#drop-area';
      if (isSampleIntent && !window.hasUploadedCustomPhoto) {
        setTimeout(() => {
          if (!window.hasUploadedCustomPhoto) {
            if (typeof window.focusCustomPhotoUpload === 'function') window.focusCustomPhotoUpload();
            if (typeof window.triggerCustomPhotoWarning === 'function') window.triggerCustomPhotoWarning();
          }
        }, 200);
      }
    }

    function checkOver20MbIntent() {
      const urlParams = new URLSearchParams(window.location.search);
      const isOver20MbParam = urlParams.get('over_20mb') === 'true';
      const isOver20MbSession = sessionStorage.getItem('wm_custom_photo_over_20mb') === 'true';

      if (isOver20MbParam || isOver20MbSession) {
        const storedName = sessionStorage.getItem('wm_custom_photo_over_20mb_name');
        sessionStorage.removeItem('wm_custom_photo_over_20mb');
        sessionStorage.removeItem('wm_custom_photo_over_20mb_name');
        setTimeout(() => {
          highlightOver20MbWarning(storedName ? { name: storedName } : null);
        }, 250);
      }
    }

    function restoreFromLocalStorage() {
      const data = localStorage.getItem('wm_custom_photo_data');
      const name = localStorage.getItem('wm_custom_photo_name');
      const type = localStorage.getItem('wm_custom_photo_type');
      
      if (data && name && (!fileInput.files || fileInput.files.length === 0)) {
        fetch(data)
          .then(res => res.blob())
          .then(blob => {
            const restoredFile = new File([blob], name, { type: type || 'image/jpeg' });
            processFile(restoredFile, true);
            checkSampleIntent();
            checkOver20MbIntent();
          })
          .catch(err => {
            console.error('Failed to restore file:', err);
            checkSampleIntent();
            checkOver20MbIntent();
          });
      } else if (!fileInput.files || fileInput.files.length === 0) {
        resetToIdle();
        checkSampleIntent();
        checkOver20MbIntent();
      } else {
        checkSampleIntent();
        checkOver20MbIntent();
      }
    }

    // Navigation back / BFCache restore
    window.addEventListener('pageshow', () => {
      setTimeout(restoreFromLocalStorage, 50);
    });
    
    restoreFromLocalStorage();
  }
  // Global helpers for sections/main-product.liquid validation
  window.hasCustomPhoto = function () {
    const dndContainers = document.querySelectorAll('[data-wm-drag-drop]');
    if (!dndContainers.length) return true; // Block not on page, proceed normally

    let hasFiles = false;
    dndContainers.forEach((container) => {
      const fileInput = container.querySelector('input[type="file"]');
      if (fileInput && fileInput.files && fileInput.files.length > 0) {
        hasFiles = true;
      }
    });
    return !!(hasFiles || window.hasUploadedCustomPhoto);
  };

  window.triggerCustomPhotoWarning = function () {
    const dndContainers = document.querySelectorAll('[data-wm-drag-drop]');
    if (!dndContainers.length) return;

    dndContainers.forEach((dndContainer) => {
      dndContainer.classList.add('has-warning');
      dndContainer.setAttribute('aria-invalid', 'true');

      const infoEl = dndContainer.querySelector('[data-wm-info], .wm-drag-drop__info');
      const originalInfo = infoEl ? (infoEl.dataset.originalText || infoEl.textContent) : '';
      const errorTextEl = dndContainer.querySelector('[data-wm-error-text]');
      const uploadRequiredMsg = (errorTextEl && errorTextEl.textContent.trim()) || 'Please upload your photo to proceed.';

      if (infoEl) {
        infoEl.textContent = uploadRequiredMsg;
        infoEl.classList.add('is-error');
      }

      const statusLiveEl = dndContainer.querySelector('[data-wm-status]');
      if (statusLiveEl) {
        statusLiveEl.textContent = uploadRequiredMsg;
      }

      const cleanup = () => {
        dndContainer.classList.remove('has-warning');
        dndContainer.setAttribute('aria-invalid', 'false');
        if (infoEl) {
          infoEl.textContent = originalInfo;
          infoEl.classList.remove('is-error');
        }
        dndContainer.removeEventListener('click', cleanup);
        dndContainer.removeEventListener('dragenter', cleanup);
        const fi = dndContainer.querySelector('input[type="file"]');
        if (fi) fi.removeEventListener('change', cleanup);
      };

      dndContainer.addEventListener('click', cleanup, { once: true });
      dndContainer.addEventListener('dragenter', cleanup, { once: true });
      const fi = dndContainer.querySelector('input[type="file"]');
      if (fi) fi.addEventListener('change', cleanup, { once: true });
    });
  };

  window.focusCustomPhotoUpload = function () {
    const dndContainer = document.querySelector('[data-wm-drag-drop]');
    if (!dndContainer) return;

    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }

    const offset = 90;
    const y = dndContainer.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: y, behavior: 'smooth' });

    const focusTarget = dndContainer.querySelector('[data-wm-drop-trigger], button, [data-wm-drop-zone]');
    if (focusTarget) focusTarget.focus({ preventScroll: true });
  };

  window.validateCustomPhotoUpload = function (options = {}) {
    if (!window.hasCustomPhoto()) {
      window.triggerCustomPhotoWarning();
      if (options.focus !== false) {
        window.focusCustomPhotoUpload();
      }
      return false;
    }
    return true;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDragDropUpload);
  } else {
    initDragDropUpload();
  }
})();